import DOMPurify from "dompurify";
import { useEffect, useState } from "react";
import PublicShell from "@/components/layout/PublicShell";
import { api } from "@/lib/api";

export default function PrivacyPolicyPage() {
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    api.get("/privacy-policy")
      .then((r) => { if (active) setPolicy(r.data); })
      .catch((e) => { if (active) setError(e?.message || "Unable to load policy"); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    document.title = policy?.title ? `${policy.title} — Nexra` : "Privacy Policy — Nexra";
  }, [policy]);

  return (
    <PublicShell>
      <section className="relative bg-obsidian text-white" data-testid="privacy-hero">
        <div className="absolute inset-0 bg-gridlines-dark opacity-40 pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-10 lg:px-16 py-12 sm:py-16 lg:py-24">
          <p className="overline mb-4 text-white/60">Legal</p>
          <h1 className="font-heading text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
            {policy?.title || "Privacy Policy"}
          </h1>
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-mono uppercase tracking-[0.2em] text-white/60">
            {policy?.version && <span data-testid="privacy-version">Version {policy.version}</span>}
            {policy?.effective_date && <span data-testid="privacy-effective-date">Effective {policy.effective_date}</span>}
          </div>
          {policy?.summary && (
            <p className="mt-6 sm:mt-8 max-w-3xl text-sm sm:text-base lg:text-lg text-white/80 leading-relaxed">
              {policy.summary}
            </p>
          )}
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-[900px] mx-auto px-5 sm:px-10 lg:px-16 py-12 sm:py-16 lg:py-24">
          {loading && (
            <div className="text-sm text-muted-foreground" data-testid="privacy-loading">Loading policy…</div>
          )}
          {error && (
            <div className="text-sm text-destructive" data-testid="privacy-error">{error}</div>
          )}
          {policy?.body_html && (
            <article
              className="prose-policy"
              data-testid="privacy-body"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(policy.body_html || "") }}
            />
          )}
        </div>
      </section>
    </PublicShell>
  );
}
